/**
 * Keeping a bulk "Generate All Variables" run's work when a session save is refused.
 *
 * Measured 2026-10-04 on Clinical Dev: the dialog said 29 of 29 forms were processed, 26 were saved,
 * and the page said the workspace "was changed elsewhere". Once one session save is refused as stale
 * the page stops writing (utils/versionedDoc.ts), so every form generated after that stayed in memory
 * only. The page now reloads the stored session and plans a recovery from three things: the forms the
 * page is SHOWING (kept, with their screen-only fields), the stored forms (taken only where they changed
 * elsewhere), and what the run generated (applied only to forms still empty in storage). A form that
 * already has variables in storage is never overwritten; a form removed elsewhere is not brought back.
 *
 * Pure: no React, no Firebase.
 */
import { formNeedsVariables } from '../../pages/project/crfBulkActions';

export const MAX_BULK_SAVE_ATTEMPTS = 3;

export interface MergeableForm {
  id: string;
  name: string;
  status: string;
  variables: unknown[] | null;
  serverUpdatedAt?: string;
}

export interface GeneratedForm<F> {
  name: string;
  updates: Partial<F>;
}

export interface RecoveryPlan<F> {
  forms: F[];
  /** Run-generated forms whose variables are re-applied (ids as shown on the page). */
  applied: string[];
  /** Names of forms the stored copy already had variables for, and they are NOT this run's; the stored variables win. */
  filledElsewhere: string[];
  /** Names of run-generated forms whose stored variables are already this run's own (saved before the refusal). */
  savedByThisRun: string[];
  /** Names of forms whose id was gone in storage and were found again by exact name. */
  matchedByName: string[];
  /** Names of generated forms removed elsewhere; they are not brought back. */
  removed: string[];
  /** Ids of other forms whose stored copy is newer than the one shown; the stored copy was taken. */
  changedElsewhere: string[];
}

/** True when two variable lists are the same questions in the same order (by id, else variable name). */
function sameVariables(stored: unknown, generated: unknown): boolean {
  if (!Array.isArray(stored) || !Array.isArray(generated) || stored.length !== generated.length) return false;
  const key = (v: unknown): string => {
    const o = (v && typeof v === 'object' ? v : {}) as { id?: unknown; varName?: unknown };
    return typeof o.id === 'string' && o.id ? 'id:' + o.id : 'name:' + String(o.varName ?? '');
  };
  return stored.every((v, i) => key(v) === key(generated[i]));
}

export function planBulkRecovery<F extends MergeableForm>(
  shown: F[],
  stored: F[],
  generated: Map<string, GeneratedForm<F>>,
): RecoveryPlan<F> {
  const storedById = new Map(stored.map((f) => [f.id, f]));
  const nameCount = new Map<string, number>();
  for (const f of stored) nameCount.set(f.name, (nameCount.get(f.name) ?? 0) + 1);
  const storedByName = new Map(stored.filter((f) => nameCount.get(f.name) === 1).map((f) => [f.name, f]));
  const shownIds = new Set(shown.map((f) => f.id));
  const plan: RecoveryPlan<F> = { forms: [], applied: [], filledElsewhere: [], savedByThisRun: [], matchedByName: [], removed: [], changedElsewhere: [] };
  const used = new Set<string>();

  for (const f of shown) {
    const g = generated.get(f.id);
    if (!g) {
      const s = storedById.get(f.id);
      if (s) used.add(s.id);
      if (s && s.serverUpdatedAt !== f.serverUpdatedAt) {
        plan.forms.push({ ...f, ...s });
        plan.changedElsewhere.push(f.id);
      } else {
        plan.forms.push(f);
      }
      continue;
    }
    let s = storedById.get(f.id);
    if (!s) {
      const byName = storedByName.get(g.name);
      if (byName && !used.has(byName.id) && !shownIds.has(byName.id)) {
        s = byName;
        plan.matchedByName.push(g.name);
      }
    }
    if (!s) { plan.removed.push(g.name); continue; }
    used.add(s.id);
    if (!formNeedsVariables(s)) {
      plan.forms.push({ ...f, ...s });
      if (sameVariables(s.variables, g.updates.variables)) plan.savedByThisRun.push(g.name);
      else plan.filledElsewhere.push(g.name);
      continue;
    }
    plan.forms.push({ ...f, ...s, ...g.updates, id: s.id });
    plan.applied.push(f.id);
  }
  // Forms added elsewhere since this page loaded.
  for (const s of stored) if (!used.has(s.id) && !shownIds.has(s.id)) plan.forms.push(s);
  return plan;
}

/** Names of run-generated forms whose STORED copy still has no variables (matched by id, then exact name). */
export function generatedFormsLeftEmpty<F extends MergeableForm>(stored: F[], generated: Map<string, GeneratedForm<F>>): string[] {
  const byId = new Map(stored.map((f) => [f.id, f]));
  const left: string[] = [];
  for (const [id, g] of generated) {
    const s = byId.get(id) ?? stored.find((x) => x.name === g.name);
    if (!s || formNeedsVariables(s)) left.push(g.name);
  }
  return left;
}

/**
 * The plain-language line for the run log after a recovery. `saved` is true only when the save after
 * the recovery was observed to succeed; otherwise it says what is not yet saved.
 */
export function describeRecovery<F>(plan: RecoveryPlan<F>, saved: boolean): string {
  const count = plan.applied.length + plan.savedByThisRun.length;
  const head = "Another open copy of this study's forms page saved the workspace while the run was going. ";
  const parts: string[] = [
    head + (saved
      ? (plan.applied.length
          ? `${count} generated form(s) are saved (${plan.applied.length} had to be saved again).`
          : `Your ${count} generated form(s) are all saved.`)
      : `${count} generated form(s) are NOT saved yet; the page will say so if saving fails again.`),
  ];
  if (plan.filledElsewhere.length) parts.push(`Filled in elsewhere during the run, so the other copy's questions were kept: ${plan.filledElsewhere.join(', ')}.`);
  if (plan.matchedByName.length) parts.push(`Matched by exact name because the form id was gone: ${plan.matchedByName.join(', ')}.`);
  if (plan.removed.length) parts.push(`Removed elsewhere, not restored: ${plan.removed.join(', ')}.`);
  return parts.join(' ');
}
